import React, { FC, useContext, useState } from 'react'
import { ChartType } from 'chart.js'
import { Trans, t } from '@lingui/macro'

import { TimeUnitEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import IconDocEnrolment from '../../../components/common/icon/IconDocEnrolment'

import ResourcesOverviewTable from './ResourcesOverviewTable'
import AnalyticsChart from '../components/AnalyticsChart'
import { graphOptions } from '../chartOptions'
import {
  AnalyticsHighlight,
  AnalyticsHighlightTextColorEnum
} from '../AnalyticsHighlight'
import Loader from '../../../components/common/loader/Loader'
import { AnalyticsHeader } from '../AnalyticsHeader'
import BorderedBox from '../../../components/common/bordered-box/BorderedBox'
import { TableExtendedHeader } from '../../../components/common/table-extended-old/table-extended-header/TableExtendedHeader'
import { AnalyticsRating } from '../components/analytics-rating/AnalyticsRating'
import { MarbleColor } from '../../../components/common/marble-rating/Marble'
import { useResourcesAnalyticsQuery } from './ResourcesAnalytics.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
import { AnalyticsFiltersEnum } from '../constants'

interface IRatingSpread {
  count: number
  rating: number
}

const getRatingCount = (
  ratingSpread: IRatingSpread[],
  targetRating: number
): number => {
  const foundRating = ratingSpread.find((singleRating) => {
    return singleRating.rating === targetRating
  })
  if (!foundRating) {
    return 0
  }
  return foundRating.count
}

interface IProps {
  showFilterPanel?: boolean
}

const Resources: FC<IProps> = ({ showFilterPanel }: IProps) => {
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const [chartTimeUnit, setChartTimeUnit] = useState<TimeUnitEnum>(
    TimeUnitEnum.DAY
  )
  const { data, loading } = useResourcesAnalyticsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      filter: filterAsQueryVariables,
      chartTimeUnit
    }
  })

  if (loading || !data?.analytics) {
    return <Loader />
  }

  const {
    analytics: {
      resources: {
        topicViewsByTimeUnit,
        totalViewsCount,
        ratingSpread,
        averageRating,
        uniqueViewedTopicsCount
      },
      workbooks: { completedCount, workbooksCount, passedWorkbooksInPercents }
    }
  } = data

  let workbooksPassRate
  let workbooksPassRateTextColor
  if (
    passedWorkbooksInPercents === null ||
    passedWorkbooksInPercents === undefined
  ) {
    workbooksPassRate = '--'
    workbooksPassRateTextColor = AnalyticsHighlightTextColorEnum.grey
  } else {
    workbooksPassRate = `${Math.round(passedWorkbooksInPercents)}%`
    workbooksPassRateTextColor = AnalyticsHighlightTextColorEnum.black
  }

  const viewsPerPerson =
    Math.round((totalViewsCount / uniqueViewedTopicsCount) * 10) / 10

  const totalRatingCount = ratingSpread.reduce((acc, rating) => {
    return acc + (rating.count ?? 0)
  }, 0)

  const renderAnalyticsBoxes = () => {
    return (
      <>
        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <div className="AnalyticsHighlight__row">
              <AnalyticsHighlight
                value={totalViewsCount}
                icon="eye"
                description={t`Resource views`}
                iconColor="blue"
              />
              <AnalyticsHighlight
                value={uniqueViewedTopicsCount}
                icon="people"
                description={t`Active resources`}
                iconColor="green"
              />
              <AnalyticsHighlight
                value={viewsPerPerson}
                icon="reload"
                description={t`Views per resource`}
                iconColor="purple"
              />
            </div>

            <AnalyticsChart
              width={800}
              height={160}
              config={{
                type: 'line' as ChartType,
                options: graphOptions
              }}
              data={[{ dataset: topicViewsByTimeUnit, color: '11,146,213' }]}
              onGranularityChanged={setChartTimeUnit}
              granularity={chartTimeUnit}
            />
          </BorderedBox>
        </div>
        <div className="AnalyticsRow">
          <BorderedBox className="AnalyticsRow__box">
            <TableExtendedHeader title={t`Ratings`} />

            <div className="AnalyticsRating__wrapper">
              <div className="AnalyticsRating__values">
                <AnalyticsRating
                  color={MarbleColor.Green}
                  total={totalRatingCount}
                  value={getRatingCount(ratingSpread, 5)}
                  order={5}
                />
                <AnalyticsRating
                  color={MarbleColor.Green}
                  total={totalRatingCount}
                  value={getRatingCount(ratingSpread, 4)}
                  order={4}
                />
                <AnalyticsRating
                  color={MarbleColor.Yellow}
                  total={totalRatingCount}
                  value={getRatingCount(ratingSpread, 3)}
                  order={3}
                />
                <AnalyticsRating
                  color={MarbleColor.Red}
                  total={totalRatingCount}
                  value={getRatingCount(ratingSpread, 2)}
                  order={2}
                />
                <AnalyticsRating
                  color={MarbleColor.Red}
                  total={totalRatingCount}
                  value={getRatingCount(ratingSpread, 1)}
                  order={1}
                />
              </div>

              <AnalyticsHighlight
                value={averageRating}
                description={t`Avg. Rating`}
              />
            </div>
          </BorderedBox>

          <BorderedBox className="AnalyticsRow__box">
            <TableExtendedHeader title={t`Enrolments`} />
            <div className="AnalyticsHighlight__row">
              <AnalyticsHighlight
                value={workbooksCount}
                icon={<IconDocEnrolment boxSize="20px" />}
                description={t`Enrolments`}
              />

              <AnalyticsHighlight
                value={completedCount}
                icon="check"
                iconColor="green"
                description={t`Completed`}
              />

              <AnalyticsHighlight
                value={workbooksPassRate}
                icon="pie-chart"
                iconColor="blue"
                description={t`Pass rate`}
                textColor={workbooksPassRateTextColor}
              />
            </div>
          </BorderedBox>
        </div>
      </>
    )
  }

  return (
    <>
      <AnalyticsHeader
        showFilterPanel={showFilterPanel}
        activeFilters={[
          AnalyticsFiltersEnum.INCLUDE_ADMINS,
          AnalyticsFiltersEnum.INCLUDE_ARCHIVED,
          AnalyticsFiltersEnum.INCLUDE_DRAFTS,
          AnalyticsFiltersEnum.DATE_FILTER,
          AnalyticsFiltersEnum.WORKSPACE_FILTER,
          AnalyticsFiltersEnum.GROUP_FILTER
        ]}
      >
        <Trans>Resources</Trans>
      </AnalyticsHeader>

      {renderAnalyticsBoxes()}
      <ResourcesOverviewTable />
    </>
  )
}

export default Resources
